import * as Sentry from '@sentry/vue'
import Utils from '@/util'

/**
 * Generic tracking function for AI smart grouping features (Word Cloud, Open Ended)
 * @param {Object} params - Tracking parameters
 * @param {string} params.featureType - Type of feature ("Word cloud", "Open ended", etc.)
 * @param {string} params.stage - Stage of the process ("request", "success", "error")
 * @param {Object} params.trackingData - All tracking data including IDs and metrics
 * @param {string} params.trackingData.eventCategory - Required: Event category
 * @param {string} params.trackingData.presentationId - Required: Presentation ID
 * @param {string} params.trackingData.userId - Required: User ID
 * @param {string} params.trackingData.slideId - Required: Slide ID
 * @param {number} [params.trackingData.submissionCount] - Optional: Number of submissions
 * @param {number} [params.trackingData.audienceCount] - Optional: Number of audience members
 * @param {number} [params.trackingData.numberOfGroups] - Optional: Number of groups created (success stage)
 * @param {string} [params.trackingData.responseTime] - Optional: API response time (success stage)
 * @param {string} [params.trackingData.errorMessage] - Optional: Error message (error stage)
 * @param {number} [params.trackingData.errorCode] - Optional: Error code (error stage)
 */
export const trackSmartGrouping = ({
  featureType,
  stage,
  trackingData,
}) => {
  try {
    const {
      eventCategory,
      presentationId,
      userId,
      slideId,
      ...otherData
    } = trackingData || {}

    const eventMap = {
      request: `${featureType} Grouping > Start grouping`,
      success: `${featureType} Grouping > Grouping completed`,
      error: `${featureType} Grouping > Grouping failed`,
    }

    // Validate stage parameter
    if (!eventMap[stage]) return

    // Validate required IDs
    if (!eventCategory || !presentationId || !userId || !slideId) return

    const eventName = eventMap[stage]
    const payload = {
      eventAction: eventName,
      eventCategory,
      eventLabel: featureType,
      presentationId,
      userId,
      slideId,
      ...otherData,
    }

    // Use the existing trackGA4AndMixpanel function
    Utils.trackGA4AndMixpanel(eventName, payload)
  } catch (trackingError) {
    const featureTag = featureType?.toLowerCase()?.replace(/ /g, '-') || ''
    Sentry.captureException(trackingError, {
      tags: { tracking: `${featureTag}-smart-grouping` },
      extra: { stage, trackingData },
    })
  }
}

/**
 * Tracking for AI spellchecker misspell fetch lifecycle
 * @param {Object} params
 * @param {'request'|'success'|'error'} params.stage - Stage of the fetch flow
 * @param {number} [params.misspellCount] - Number of misspells returned
 * @param {Error} [params.error] - Error instance captured during the fetch
 */
export const trackSpellcheckerFetch = ({
  stage,
  misspellCount,
  error,
}) => {
  try {
    const eventMap = {
      request: 'Spellchecker > Fetch misspell list > Started',
      success: 'Spellchecker > Fetch misspell list > Succeeded',
      error: 'Spellchecker > Fetch misspell list > Failed',
    }

    if (!eventMap[stage]) return

    const eventName = eventMap[stage]
    const payload = {
      eventAction: eventName,
      eventCategory: 'AI Spellchecker',
      eventLabel: 'Fetch misspell list',
    }

    if (typeof misspellCount === 'number') {
      payload.misspellCount = misspellCount
    }

    if (error) {
      payload.error = error
    }

    Utils.trackGA4AndMixpanel(eventName, payload)
  } catch (trackingError) {
    Sentry.captureException(trackingError, {
      tags: { tracking: 'ai-spellchecker-fetch' },
      extra: {
        stage,
        misspellCount,
        error,
      },
    })
  }
}

/**
 * Tracking for AI Readiness Checker lifecycle
 * @param {Object} params
 * @param {'trigger'|'fetchIssues'|'resolveIssues'|'ignoreIssue'} params.action - Action being tracked
 * @param {'started'|'succeeded'|'failed'} params.stage - Stage of the action
 * @param {Error} [params.error] - Error instance captured on failure
 */
export const trackReadinessChecker = ({
  action,
  stage,
  error,
}) => {
  try {
    const eventMap = {
      trigger: {
        started: 'Readiness Checker > Trigger > Started',
        failed: 'Readiness Checker > Trigger > Failed',
      },
      fetchIssues: {
        started: 'Readiness Checker > Fetch Issues > Started',
        failed: 'Readiness Checker > Fetch Issues > Failed',
      },
      resolveIssues: {
        started: 'Readiness Checker > Resolve Issues > Started',
        succeeded: 'Readiness Checker > Resolve Issues > Succeeded',
        failed: 'Readiness Checker > Resolve Issues > Failed',
      },
      ignoreIssue: {
        started: 'Readiness Checker > Ignore Issue > Started',
        succeeded: 'Readiness Checker > Ignore Issue > Succeeded',
        failed: 'Readiness Checker > Ignore Issue > Failed',
      },
    }

    const eventName = eventMap[action]?.[stage]
    if (!eventName) return

    const payload = {
      eventAction: eventName,
      eventCategory: 'AI Readiness Checker',
    }

    if (error) {
      payload.error = error
    }

    Utils.trackGA4AndMixpanel(eventName, payload)
  } catch (trackingError) {
    Sentry.captureException(trackingError, {
      tags: { tracking: 'ai-readiness-checker' },
      extra: { action, stage, error },
    })
  }
}

/**
 * Tracking for Feature Settings fetch lifecycle (used for AI consent gates)
 * @param {Object} params
 * @param {'started'|'failed'} params.stage - Stage of the fetch flow
 * @param {Error} [params.error] - Error instance captured on failure
 */
export const trackFeatureSettings = ({
  stage,
  error,
}) => {
  try {
    const eventMap = {
      started: 'Feature Settings > Fetch > Started',
      failed: 'Feature Settings > Fetch > Failed',
    }

    const eventName = eventMap[stage]
    if (!eventName) return

    const payload = {
      eventAction: eventName,
      eventCategory: 'AI Feature Settings',
    }

    if (error) {
      payload.error = error
    }

    Utils.trackGA4AndMixpanel(eventName, payload)
  } catch (trackingError) {
    Sentry.captureException(trackingError, {
      tags: { tracking: 'ai-feature-settings' },
      extra: { stage, error },
    })
  }
}

/**
 * Tracking for AI Insights fetch lifecycle
 * @param {Object} params
 * @param {'started'|'succeeded'|'failed'} params.stage - Stage of the fetch flow
 * @param {Error} [params.error] - Error instance captured on failure
 */
export const trackAIInsights = ({
  stage,
  error,
}) => {
  try {
    const eventMap = {
      started: 'AI Insights > Fetch > Started',
      succeeded: 'AI Insights > Fetch > Succeeded',
      failed: 'AI Insights > Fetch > Failed',
    }

    const eventName = eventMap[stage]
    if (!eventName) return

    const payload = {
      eventAction: eventName,
      eventCategory: 'AI Insights',
    }

    if (error) {
      payload.error = error
    }

    Utils.trackGA4AndMixpanel(eventName, payload)
  } catch (trackingError) {
    Sentry.captureException(trackingError, {
      tags: { tracking: 'ai-insights' },
      extra: { stage, error },
    })
  }
}
